import type { SVGProps } from 'react';
import { useAuthor } from '@/hooks/useAuthor';
import {
  getShowcasePubkey,
  NAV_LINKS,
  SOCIAL_LINKS,
} from '@/lib/showcase';
import { getDisplayName } from '@/lib/getDisplayName';
import {
  InstagramIcon,
  YoutubeIcon,
  FacebookIcon,
  XIcon,
} from '@/components/BrandIcons';
import { cn } from '@/lib/utils';

const SOCIAL_ICONS: Record<
  string,
  (props: SVGProps<SVGSVGElement>) => React.JSX.Element
> = {
  Instagram: InstagramIcon,
  YouTube: YoutubeIcon,
  Facebook: FacebookIcon,
  X: XIcon,
};

export function SiteFooter() {
  const pubkey = getShowcasePubkey();
  const author = useAuthor(pubkey);
  const metadata = author.data?.metadata;
  const title = metadata?.display_name || getDisplayName(metadata, pubkey);
  const year = new Date().getFullYear();

  return (
    <footer className="mt-20 border-t border-border bg-secondary/40">
      <div className="mx-auto max-w-5xl px-4 py-14">
        <div className="flex flex-col items-center text-center">
          <h2 className="font-display text-2xl font-bold">{title}</h2>
          {metadata?.about && (
            <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-muted-foreground">
              {metadata.about}
            </p>
          )}

          {/* Nav links */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-3">
            {NAV_LINKS.map((link) => (
              <a
                key={link.label}
                href={link.href}
                {...(link.external
                  ? { target: '_blank', rel: 'noopener noreferrer' }
                  : {})}
                className={cn(
                  'text-sm font-medium uppercase tracking-wide transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                  link.variant === 'button'
                    ? 'inline-flex items-center rounded-full bg-primary px-4 py-1.5 text-primary-foreground shadow-sm hover:bg-primary/90'
                    : 'text-foreground/80 hover:text-primary',
                )}
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Social icons */}
          <div className="mt-6 flex items-center justify-center gap-3">
            {SOCIAL_LINKS.map((link) => {
              const Icon = SOCIAL_ICONS[link.label];
              return (
                <a
                  key={link.label}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={link.label}
                  className="flex size-10 items-center justify-center rounded-full border border-border bg-background text-foreground/70 transition-colors hover:border-primary hover:text-primary"
                >
                  {Icon ? <Icon className="size-5" /> : link.label[0]}
                </a>
              );
            })}
          </div>

          {/* Copyright */}
          <p className="mt-8 text-xs text-muted-foreground">
            © {year} {title}. Powered by Nostr.
          </p>
          <a
            href="https://shakespeare.diy"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1 text-xs text-muted-foreground/70 transition-colors hover:text-foreground"
          >
            Vibed with Shakespeare
          </a>
        </div>
      </div>
    </footer>
  );
}
